import { ImageProcessingState } from "@nova/constants"
import { trimMiddle } from "@nova/util-strings"
import classNames from "classnames"
import { selectActiveImageIds, selectHoveredImageID } from "features/map/layers/selectors"
import { setActiveImageId, setHoveredImageId } from "features/map/layers/slice"
import { useAppSelector } from "integrations/redux/hooks"
import { get } from "nodash"
import { MouseEvent, useCallback, useEffect, useRef, useState } from "react"
import { useDebounceDispatch } from "tools/store"
import { useUploadFlowContext } from "../useUploadFlowContext"

export const UploadFlowList = ({
    removeSelected,
    completeStates,
}: {
    removeSelected: () => Promise<void>
    completeStates: ImageProcessingState[]
}) => {
    const { uploads, imagePreviewType } = useUploadFlowContext()
    const { dispatch, debounceDispatch } = useDebounceDispatch(50)
    const activeImageIds = useAppSelector(selectActiveImageIds)
    const hoveredImageId = useAppSelector(selectHoveredImageID)

    const containerRef = useRef<HTMLDivElement>(null)
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
    const lastSelectedIndex = useRef<number | null>(null)

    const itemRefs = useRef<(HTMLDivElement | null)[]>([])

    //ensures the focused item is in view
    useEffect(() => {
        if (focusedIndex !== null && itemRefs.current[focusedIndex]) {
            itemRefs.current[focusedIndex]?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            })
        }
    }, [focusedIndex])

    //sets up keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!uploads.length || !imagePreviewType) return

            const maxIndex = uploads.length - 1

            if (focusedIndex === null && uploads.length > 0) {
                setFocusedIndex(0)
                return
            }

            const updateSelection = (newIndex: number, shift: boolean) => {
                setFocusedIndex(newIndex)
                if (shift && lastSelectedIndex.current !== null) {
                    const range = [lastSelectedIndex.current, newIndex].sort((a, b) => a - b)
                    const rangeSelection = Array.from(
                        { length: range[1] - range[0] + 1 },
                        (_, i) => i + range[0]
                    )
                    const ids = Object.fromEntries(rangeSelection.map((i) => [uploads[i].id, true]))
                    dispatch(setActiveImageId({ ids, type: imagePreviewType }))
                } else {
                    lastSelectedIndex.current = newIndex
                    dispatch(
                        setActiveImageId({
                            ids: { [uploads[newIndex].id]: true },
                            type: imagePreviewType,
                        })
                    )
                }
            }

            switch (e.key) {
                case "ArrowUp":
                    e.preventDefault()
                    if (focusedIndex! > 0) updateSelection(focusedIndex! - 1, e.shiftKey)
                    break
                case "ArrowDown":
                    e.preventDefault()
                    if (focusedIndex! < maxIndex) updateSelection(focusedIndex! + 1, e.shiftKey)
                    break
                case "Enter":
                case " ":
                    e.preventDefault()
                    if (focusedIndex !== null) {
                        const id = uploads[focusedIndex].id
                        dispatch(setActiveImageId({ ids: { [id]: true }, type: imagePreviewType }))
                    }
                    break
                case "Backspace":
                case "Delete":
                    e.preventDefault()
                    removeSelected()
                    setFocusedIndex(null)
                    lastSelectedIndex.current = null
                    break
            }
        }

        window.addEventListener("keydown", handleKeyDown)
        return () => window.removeEventListener("keydown", handleKeyDown)
    }, [focusedIndex, uploads, imagePreviewType, removeSelected, dispatch])

    //sets up click handlers
    const handleClick = useCallback(
        (e: MouseEvent<HTMLDivElement>, idx: number) => {
            if (!imagePreviewType) return
            setFocusedIndex(idx)

            if (e.shiftKey && lastSelectedIndex.current !== null) {
                const range = [lastSelectedIndex.current, idx].sort((a, b) => a - b)
                const rangeSelection = Array.from(
                    { length: range[1] - range[0] + 1 },
                    (_, i) => i + range[0]
                )
                const ids = Object.fromEntries(rangeSelection.map((i) => [uploads[i].id, true]))
                dispatch(setActiveImageId({ ids, type: imagePreviewType }))
            } else if ((e.ctrlKey || e.altKey) && lastSelectedIndex.current !== null) {
                const ids = { ...activeImageIds, [uploads[idx].id]: true }
                dispatch(setActiveImageId({ ids, type: imagePreviewType }))
            } else {
                dispatch(
                    setActiveImageId({
                        ids: { [uploads[idx].id]: true },
                        type: imagePreviewType,
                    })
                )
                lastSelectedIndex.current = idx
            }
        },
        [uploads, activeImageIds, imagePreviewType, dispatch]
    )

    return (
        <div
            className="flex flex-col gap-1 w-full outline-none"
            aria-multiselectable="true"
            ref={containerRef}
            tabIndex={0}
        >
            {uploads.map((upload, idx) => {
                const fileName = get(upload, "fileName", "")
                const isHovered = upload.id === hoveredImageId
                const isActive = !!activeImageIds[upload.id]
                const status = get(upload, "state") as ImageProcessingState
                const isUploaded = completeStates.includes(status)
                const isUploading =
                    !isUploaded &&
                    // Do || on all cases that isUploading could be true. If `isUploaded` is true,
                    // this short circuits to false anyway.
                    (status === ImageProcessingState.Uploading ||
                        status === ImageProcessingState.UploadDone)
                const isErrored = !!get(upload, "error")
                const warningColor = get(upload, "warningColor", "")
                return (
                    <div
                        key={upload.id}
                        ref={(el) => {
                            itemRefs.current[idx] = el
                        }}
                        className={classNames(
                            "flex items-center gap-2 w-[98%] rounded-md cursor-pointer px-2 py-1 select-none text-gray-100",
                            {
                                "bg-gray-700": isHovered,
                                "bg-blue-700 !text-gray-0": isActive,
                                "hover:bg-gray-700 hover:text-gray-0": !isActive,
                            }
                        )}
                        onMouseEnter={() => debounceDispatch(setHoveredImageId(upload.id))}
                        onMouseLeave={() => debounceDispatch(setHoveredImageId(undefined))}
                        onClick={(e) => handleClick(e, idx)}
                        onContextMenu={(e) => {
                            e.preventDefault()
                            handleClick(e, idx)
                        }}
                    >
                        {(isUploading || isUploaded || isErrored || !!warningColor) && (
                            <div
                                className={classNames("h-2 w-2 rounded-3xl", {
                                    "bg-green-500": isUploaded && !isErrored,
                                    "bg-green-200": isUploading && !isErrored,
                                    "bg-red-500": isErrored,
                                })}
                                style={warningColor ? { backgroundColor: warningColor } : undefined}
                            />
                        )}
                        <small>{trimMiddle(fileName, { maxLength: 27 })}</small>
                    </div>
                )
            })}
        </div>
    )
}
